/** localStorage key holding all local article drafts. */
export const ARTICLE_DRAFTS_KEY = 'wikistar:article-drafts';

/**
 * An unpublished article kept only on this device.
 *
 * Drafts are keyed by a generated `id` rather than by the article's
 * slug (the way the offline library keys saved articles by
 * `pubkey:d`). A draft is a work in progress: it may have no title at
 * all yet, its title may change repeatedly while writing, and a writer
 * may legitimately keep two different drafts that would normalize to
 * the same slug. A stable synthetic id survives all of that, whereas a
 * slug key would silently merge or lose drafts as the title is edited.
 */
export interface ArticleDraft {
  id: string;
  /**
   * The author this draft belongs to. Drafts live in shared browser
   * storage, but they're written *as* a specific Nostr identity, so
   * they're scoped per-pubkey rather than shown to whoever happens to
   * be logged in on this device.
   */
  pubkey: string;
  title: string;
  summary: string;
  content: string;
  /** Normalized slug at last save — used to build the editor URL. May be empty for an untitled draft. */
  slug: string;
  createdAt: number;
  updatedAt: number;
  /**
   * The `id` of the NIP-59 gift-wrap event this draft was most recently
   * synced to Nostr as — kept locally so a deletion can reference it with
   * a NIP-09 delete and actually garbage-collect the synced copy. Not
   * every local draft has one (only the ones the user has synced).
   */
  wrapId?: string;
  /** When this draft was most recently pushed to Nostr (ms since epoch). */
  syncedAt?: number;
  /** Which relays the last successful sync confirmed. Stored per-draft so a card can show "synced to N relays" without another query. */
  syncedRelays?: string[];
}

export type ArticleDraftStore = Record<string, ArticleDraft>;

export function newDraftId(): string {
  // randomUUID needs a secure context; fall back for http:// dev hosts.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export class DraftStorageError extends Error {
  constructor() {
    super('Not enough browser storage left to save this draft.');
    this.name = 'DraftStorageError';
  }
}

/**
 * Reads and validates the stored drafts.
 *
 * localStorage is user/extension-writable and may hold data from an
 * older version of the app, so every field is re-validated rather than
 * trusted — a malformed entry is dropped instead of being allowed to
 * load into the editor as `undefined` and wipe out real work.
 */
export function readArticleDrafts(): ArticleDraftStore {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(ARTICLE_DRAFTS_KEY);
  } catch {
    // Storage can be unavailable entirely (Safari private mode, etc.).
    return {};
  }
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const out: ArticleDraftStore = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const draft = value as Partial<ArticleDraft>;
      if (typeof draft.id !== 'string' || !draft.id) continue;
      if (typeof draft.pubkey !== 'string' || !draft.pubkey) continue;

      out[key] = {
        id: draft.id,
        pubkey: draft.pubkey,
        title: typeof draft.title === 'string' ? draft.title : '',
        summary: typeof draft.summary === 'string' ? draft.summary : '',
        content: typeof draft.content === 'string' ? draft.content : '',
        slug: typeof draft.slug === 'string' ? draft.slug : '',
        createdAt: typeof draft.createdAt === 'number' ? draft.createdAt : 0,
        updatedAt: typeof draft.updatedAt === 'number' ? draft.updatedAt : 0,
        wrapId: typeof draft.wrapId === 'string' ? draft.wrapId : undefined,
        syncedAt: typeof draft.syncedAt === 'number' ? draft.syncedAt : undefined,
        syncedRelays: Array.isArray(draft.syncedRelays)
          ? draft.syncedRelays.filter((r): r is string => typeof r === 'string')
          : undefined,
      };
    }
    return out;
  } catch {
    return {};
  }
}

export function writeArticleDrafts(store: ArticleDraftStore): void {
  try {
    localStorage.setItem(ARTICLE_DRAFTS_KEY, JSON.stringify(store));
  } catch (error) {
    if (error instanceof DOMException) throw new DraftStorageError();
    throw error;
  }
}

/** A draft's display title, falling back to its slug and then a placeholder. */
export function draftTitle(draft: ArticleDraft): string {
  return draft.title.trim() || draft.slug || 'Untitled draft';
}

/** True when a draft has nothing worth keeping — used to avoid saving empty shells. */
export function isDraftEmpty(draft: Pick<ArticleDraft, 'title' | 'summary' | 'content'>): boolean {
  return !draft.title.trim() && !draft.summary.trim() && !draft.content.trim();
}
